import Base2Repository from "./base2.repository";
import { GenericPersistence } from "core/data/persistence";
import { GenericService } from "core/data/service";
import { CompanyRepository } from "core/repository";
import Big from "big.js";
import Moment from "moment";

class ProductSalesReportRepository extends Base2Repository {
  getEntityIdentifier() {
    return "product-sales-report";
  }

  getPersistenceProvider() {
    return new GenericPersistence(this.getEntityIdentifier());
  }

  getServiceProvider() {
    return new GenericService({
      fetch: "/v1/product_sale_reports"
    });
  }

  async getReportDatatable({
    filters = [],
    order = [],
    pagination = { page: 1, perPage: 25 },
    group = false,
    transforms = []
  }) {
    const results = await this.fetch({
      filters,
      order,
      pagination,
      group,
      transforms
    });

    if (!results.success) {
      return { success: false };
    }

    results.data.forEach(data => {
      data.currentRevenues = Big(data.revenues)
        .plus(data.tips || 0)
        .plus(data.total_adding_optional || 0)
        .minus(data.credit_coupon || 0);
      data.currentProfit = Big(data.currentRevenues).minus(data.costs);
      if (
        parseInt(data.currentProfit, 10) <= 0 ||
        parseInt(data.currentRevenues, 10) <= 0
      ) {
        data.profitInPercentage = 0;
      } else if (
        parseInt(data.currentRevenues, 10) > 0 &&
        parseInt(data.costs, 10) === 0
      ) {
        data.profitInPercentage = 1;
      } else {
        data.profitInPercentage = Big(data.currentRevenues)
          .minus(data.costs)
          .div(data.currentRevenues);
      }

      return data;
    });

    return {
      success: true,
      data: results.data,
      total: results.total,
      perPage: results.perPage
    };
  }

  async getReportSummary(filters) {
    const revenuesP = this.fetch({
      filters,
      transforms: [{ field: "revenues", operation: "SUM" }]
    });
    const costsP = this.fetch({
      filters,
      transforms: [{ field: "costs", operation: "SUM" }]
    });
    const couponP = this.fetch({
      filters,
      transforms: [{ field: "credit_coupon", operation: "SUM" }]
    });
    const tipsP = this.fetch({
      filters,
      transforms: [{ field: "tips", operation: "SUM" }]
    });
    const totalAddingOptionalP = this.fetch({
      filters,
      transforms: [{ field: "total_adding_optional", operation: "SUM" }]
    });

    const [
      revenuesR,
      costsR,
      couponR,
      tipsR,
      totalAddingOptionalR
    ] = await Promise.all([
      revenuesP,
      costsP,
      couponP,
      tipsP,
      totalAddingOptionalP
    ]);

    if (!revenuesR.success || !costsR.success) {
      return { success: false };
    }

    const revenues = revenuesR.data.result;
    const costs = costsR.data.result;
    const coupon = couponR.data.result || 0;
    const tips = tipsR.data.result;
    const totalAddingOptional = totalAddingOptionalR.data.result;

    return {
      success: true,
      data: {
        revenues: Big(revenues)
          .minus(coupon)
          .plus(tips || 0)
          .plus(totalAddingOptional),
        costs,
        profit: Big(revenues)
          .minus(coupon)
          .minus(costs)
          .plus(tips || 0)
          .plus(totalAddingOptional)
      }
    };
  }

  async getStats(filters) {
    const totalsP = this.fetch({
      filters,
      order: [{ field: "created_at", direction: "ASC" }],
      group: {
        field: "sale_uuid!company_uuid",
        transformations: [
          { field: "count", operation: "COUNT" },
          { field: "revenues", operation: "SUM" },
          { field: "sold_amount", operation: "SUM" },
          { field: "tips", operation: "SUM" },
          { field: "total_adding_optional", operation: "SUM" },
          { field: "discounts", operation: "SUM" },
          { field: "credit_coupon", operation: "SUM" }
        ]
      }
    });

    const discountsP = this.fetch({
      filters: [
        ...filters,
        { connector: "AND", field: "discounts", operation: "GT", value: "0" }
      ],
      order: [{ field: "created_at", direction: "ASC" }],
      group: {
        field: "sale_uuid!company_uuid",
        transformations: [
          { field: "count", operation: "COUNT" },
          { field: "discount_percentage", operation: "SUM" }
        ]
      }
    });

    const [totalsR, discountsR] = await Promise.all([totalsP, discountsP]);

    if (!totalsR.success || !discountsR.success) {
      return { success: false };
    }

    const totals = totalsR.data[0] || {};
    const discounts = discountsR.data[0] || {};

    const summary = {
      totalSales: totals.count || 0,
      salesWithDiscount: discounts.count || 0
    };

    if (totals.count > 0) {
      summary.averageRevenues = Big(totals.revenues)
        .plus(totals.total_adding_optional)
        .plus(parseInt(totals.tips, 10) || 0)
        .minus(totals.credit_coupon)
        .div(totals.count);
    } else {
      summary.averageRevenues = 0;
    }

    if (summary.salesWithDiscount > 0) {
      summary.averageDiscount = Big(discounts.discount_percentage)
        .div(summary.salesWithDiscount)
        .times(100);
    } else {
      summary.averageDiscount = Big(0);
    }

    if (summary.totalSales > 0) {
      summary.averageProductsPerSale = Big(totals.sold_amount).div(
        summary.totalSales
      );
    } else {
      summary.averageProductsPerSale = 0;
    }

    return { success: true, data: summary };
  }

  async graph(interval, rawFrom, rawTo, filters = []) {
    const from = Moment(rawFrom).startOf(interval);
    const to = Moment(rawTo).endOf(interval);
    const steps = Math.ceil(to.diff(from, interval, true));

    const result = {
      discounts: [],
      costs: [],
      revenues: [],
      profit: [],
      totalRevenues: Big(0),
      totalCosts: Big(0),
      totalProfit: Big(0),
      profitMargin: Big(0),
      creditCoupon: Big(0),
      totalDiscounts: Big(0)
    };

    for (let i = 0; i < steps; i++) {
      const periodStart = Moment(from);
      const periodEnd = Moment(from).add(1, interval);

      result.costs.push({ from: periodStart, to: periodEnd, value: Big(0) });
      result.revenues.push({ from: periodStart, to: periodEnd, value: Big(0) });
      result.profit.push({ from: periodStart, to: periodEnd, value: Big(0) });
      result.discounts.push({
        from: periodStart,
        to: periodEnd,
        value: Big(0)
      });

      from.add(1, interval);
    }

    const totalsPromises = [];

    for (let i = 0; i < steps; i++) {
      const periodStart = result.costs[i].from;
      const periodEnd = result.costs[i].to;

      const totalsP = this.fetch({
        filters: [
          ...filters,
          {
            connector: "AND",
            field: "sale.status",
            operation: "EQ",
            value: "active"
          },
          {
            connector: "AND",
            field: "sale.activated_at",
            operation: "GTE",
            value: periodStart.toISOString()
          },
          {
            connector: "AND",
            field: "sale.activated_at",
            operation: "LTE",
            value: periodEnd.toISOString()
          }
        ],
        order: [{ field: "created_at", direction: "ASC" }],
        group: {
          field: "company_uuid",
          transformations: [
            { field: "tips", operation: "SUM" },
            { field: "total_adding_optional", operation: "SUM" },
            { field: "revenues", operation: "SUM" },
            { field: "costs", operation: "SUM" },
            { field: "profit", operation: "SUM" },
            { field: "credit_coupon", operation: "SUM" },
            { field: "discounts", operation: "SUM" }
          ]
        }
      });

      totalsPromises.push(totalsP);
    }

    const totalsResults = await Promise.all(totalsPromises);
    for (let i = 0; i < steps; i++) {
      const totalsR = totalsResults[i];

      if (!totalsR.success) {
        return { success: false };
      }

      const totals = totalsR.data[0] || {};
      result.costs[i].value = Big(totals.costs || 0);
      result.revenues[i].value = Big(totals.revenues || 0)
        .plus(totals.tips || 0)
        .plus(totals.total_adding_optional || 0)
        .minus(totals.credit_coupon || 0);
      result.profit[i].value = Big(result.revenues[i].value).minus(
        result.costs[i].value
      );
      result.discounts[i].value = Big(totals.discounts || 0);
      result.totalCosts = result.totalCosts.add(result.costs[i].value);
      result.totalRevenues = result.totalRevenues.add(result.revenues[i].value);
      result.totalProfit = result.totalProfit.add(result.profit[i].value);
      result.totalDiscounts = result.totalDiscounts.add(
        result.discounts[i].value
      );
    }
    if (!result.totalRevenues.eq(0)) {
      result.profitMargin = result.totalProfit
        .div(result.totalRevenues)
        .times(100);
    }

    return { success: true, data: result };
  }

  async getMultilocalityTotalRevenues({ filters }) {
    const productResultsRevenues = await this.fetch({
      filters,
      transforms: [{ field: "revenues", operation: "SUM" }]
    });
    const productResultsTips = await this.fetch({
      filters,
      transforms: [{ field: "tips", operation: "SUM" }]
    });
    const productResultsTotalAddingOptional = await this.fetch({
      filters,
      transforms: [{ field: "total_adding_optional", operation: "SUM" }]
    });
    const productResultsTotalCupon = await this.fetch({
      filters,
      transforms: [{ field: "credit_coupon", operation: "SUM" }]
    });

    if (
      !productResultsRevenues.success &&
      !productResultsTips.success &&
      !productResultsTotalAddingOptional.success &&
      !productResultsTotalCupon.success
    ) {
      return {
        success: false,
        userCancelled: !!productResultsRevenues.userCancelled
      };
    }

    const total =
      productResultsRevenues.data.result +
      parseInt(productResultsTips.data.result, 10) +
      productResultsTotalAddingOptional.data.result -
      productResultsTotalCupon.data.result;

    return { success: true, data: total };
  }

  async getMultilocalityChartData({ filters }) {
    let page = 1;
    let pageResults = [];
    let results = [];

    do {
      const productResults = await this.fetch({
        order: [{ field: "revenues", direction: "DESC" }],
        pagination: { page },
        filters,
        group: {
          field: "company_uuid",
          transformations: [
            { field: "company_uuid", operation: "LAST" },
            { field: "revenues", operation: "SUM" },
            { field: "tips", operation: "SUM" },
            { field: "total_adding_optional", operation: "SUM" }
          ]
        }
      });

      if (!productResults.success) {
        return {
          success: false,
          userCancelled: !!productResults.userCancelled
        };
      }

      pageResults = productResults.data;

      results = [...results, ...pageResults];
      page++;
    } while (pageResults.length > 0);

    const data = await this._assembleMultilocalityData(results);
    data.forEach(
      data =>
        (data.totals = Big(data.revenues)
          .plus(data.tips)
          .plus(data.totalAddingOptional))
    );

    return { success: true, data };
  }

  async _assembleMultilocalityData(productsResults) {
    return await productsResults.reduce(async (data$, productResult) => {
      const companyResult = await CompanyRepository.get(
        productResult.company_uuid
      );
      const companyName = companyResult.success ? companyResult.data.name : "";
      const companySlug = companyResult.success ? companyResult.data.slug : "";
      const data = await data$;
      return [
        ...data,
        {
          company: { name: companyName, slug: companySlug },
          revenues: productResult.revenues,
          tips: productResult.tips,
          totalAddingOptional: productResult.total_adding_optional,
          totals: 0
        }
      ];
    }, Promise.resolve([]));
  }

  async getMultilocalityReport({ pagination, filters }) {
    pagination.perPage = 100;
    const saleResults$ = this.fetch({
      pagination,
      filters,
      group: {
        field: "sale_uuid!company_uuid",
        transformations: [
          { field: "company_uuid", operation: "LAST" },
          { field: "sold_amount", operation: "COUNT" }
        ]
      }
    });

    const productResults$ = this.fetch({
      order: [{ field: "revenues", direction: "DESC" }],
      pagination,
      filters,
      group: {
        field: "company_uuid",
        transformations: [
          { field: "company_uuid", operation: "LAST" },
          { field: "sold_amount", operation: "SUM" },
          { field: "revenues", operation: "SUM" },
          { field: "tips", operation: "SUM" },
          { field: "discounts", operation: "SUM" },
          { field: "credit_coupon", operation: "SUM" },
          { field: "total_adding_optional", operation: "SUM" }
        ]
      }
    });

    const [saleResults, productResults] = await Promise.all([
      saleResults$,
      productResults$
    ]);

    if (!saleResults.success || !productResults.success) {
      return { success: false, userCancelled: !!productResults.userCancelled };
    }

    const data = await this._assembleMultilocalityReport(
      saleResults.data,
      productResults.data
    );
    return {
      success: true,
      data,
      total: saleResults.total,
      perPage: saleResults.perPage
    };
  }

  getMultilocalityReportStream({ filters }) {
    return async callback => {
      let page = 1;
      let hasResults;

      do {
        const result = await this.getMultilocalityReport({
          filters,
          pagination: { page }
        });

        if (!result.success) {
          return false;
        }

        hasResults = result.data.length > 0;
        result.data.forEach(line => callback(line));

        page++;
      } while (hasResults);

      return true;
    };
  }

  async _assembleMultilocalityReport(salesResults, productsResults) {
    return await productsResults.reduce(async (report$, productResult) => {
      const companyResult = await CompanyRepository.get(
        productResult.company_uuid
      );
      const companySlug = companyResult.success ? companyResult.data.slug : "";
      const companyName = companyResult.success ? companyResult.data.name : "";
      const report = await report$;
      const saleResult = salesResults.find(
        res => res.company_uuid === productResult.company_uuid
      ) || { company_uuid: productResult.company_uuid, sold_amount: 0 };
      return [
        ...report,
        {
          company: { name: companyName, slug: companySlug },
          saleCount: saleResult ? saleResult.sold_amount : 0,
          avgProductCount:
            saleResult.sold_amount > 0
              ? Big(productResult.sold_amount).div(saleResult.sold_amount)
              : 0,
          subTotalSale:
            productResult.revenues +
            productResult.discounts +
            parseInt(productResult.tips, 10) +
            productResult.total_adding_optional,
          tips: productResult.tips,
          discounts: productResult.discounts,
          creditCoupon: productResult.credit_coupon,
          averageRevenues:
            saleResult.sold_amount > 0
              ? Big(productResult.revenues)
                  .plus(parseInt(productResult.tips, 10) || 0)
                  .plus(productResult.total_adding_optional)
                  .div(saleResult.sold_amount)
              : 0,
          revenues:
            productResult.revenues +
            productResult.total_adding_optional +
            parseInt(productResult.tips, 10)
        }
      ];
    }, Promise.resolve([]));
  }
}

export default new ProductSalesReportRepository();



// WEBPACK FOOTER //
// ./src/core/repository/product-sales-report.repository.js